import { useEffect, useState } from "react"

import searchParams from "../searchParams"
import { TConfig } from "../searchParams/config.ts"
import { defaultStoreData, sharedKeys, TStore } from "./store.config.ts"

const makeKey = (key: string) => `Moogli-extranet2-${key}`

type TSearchParamCallback<T> = ((value: T) => void) | false

const localStore = {
  ...localStorage,
  getItem<K extends keyof TStore>(key: K, searchParamCallback?: TSearchParamCallback<TStore[K]>): TStore[K] {
    // eslint-disable-next-line no-restricted-syntax
    const dataStorage = localStorage.getItem(makeKey(key))

    if (searchParamCallback !== false && sharedKeys.includes(key as keyof TConfig)) {
      const dataSearch = searchParams.get(key as keyof TConfig) as TStore[K]
      if (dataSearch !== undefined) {
        if (dataStorage && JSON.parse(dataStorage) !== dataSearch && searchParamCallback) {
          searchParamCallback(dataSearch)
        }
        return dataSearch
      }
    }

    if (dataStorage === null) return defaultStoreData[key]

    return JSON.parse(dataStorage) as TStore[K]
  },
  setItem<K extends keyof TStore>(key: K, value: TStore[K]) {
    if (value === localStore.getItem(key, false)) return

    // eslint-disable-next-line no-restricted-syntax
    localStorage.setItem(makeKey(key), JSON.stringify(value))

    if (sharedKeys.includes(key as keyof TConfig))
      searchParams.set(key as keyof TConfig, value as TConfig[keyof TConfig])
  },
}

export function useLocalStoreState<K extends keyof TStore>(
  key: K,
  {
    initialState,
    searchParamCallback,
  }: { initialState?: TStore[K]; searchParamCallback?: TSearchParamCallback<TStore[K]> } = {},
) {
  const [store, setStore] = useState(
    initialState !== undefined ? initialState : localStore.getItem(key, searchParamCallback),
  )

  /* sync across tabs?
  useEffect(() => {
    const handler = (event: StorageEvent) => {
      if (!event.newValue || !event.key || event.key !== makeKey(key)) return

      setStore(JSON.parse(event.newValue))
    }
    addEventListener("storage", handler)

    return () => removeEventListener("storage", handler)
  }, [key])
  */

  useEffect(() => {
    if (store !== undefined) localStore.setItem(key, store)
  }, [key, store])

  return [store, setStore] as const
}

export default localStore
